import React, { useState, useEffect } from 'react';
import {
  makeStyles,
  mergeClasses,
  shorthands,
  Text,
  Input,
  Field,
  Button,
  RadioGroup,
  Radio,
  Textarea,
  Card,
  CardHeader,
  Badge,
  Checkbox,
  Spinner,
  Dropdown,
  Option,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Link,
  Divider,
} from '@fluentui/react-components';
import { MessageBar, MessageBarBody } from '../Common/ScrollableMessageBar';
import {
  Sparkle24Regular,
  Shield24Regular,
  Globe24Regular,
  CheckmarkCircle24Filled,
  PeopleTeam24Regular,
} from '@fluentui/react-icons';
import { UserGroupSelectorWithRadio, SelectedEntity } from '../UserGroupSelectorWithRadio';
import { domainService } from '../../services/domainService';
import { Domain } from '../../types/dataLayer';
import { configService } from '../../services/configService';
import { gfcPolicyService } from '../../services/gfcPolicyService';
import { apiClient } from '../../services/apiClient';
import { CommunicationModeSelector } from '../GFC/CommunicationModeSelector';
import { EnhancedDomainManager } from '../GFC/EnhancedDomainManager';
import { ExistingPolicySelector } from '../GFC/ExistingPolicySelector';
import { RiskCategorySelector } from '../GFC/RiskCategorySelector';
import { DomainOptimizer } from '../GFC/DomainOptimizer';
import { CommunicationWithExternalOrgs, ExistingTeamsPolicy } from '../../services/gfcPolicyService';
import useEventTracker from '../../hooks/useEventTracker';
import { ElementButton, PageName } from '../../tracking/tracking.types';
import { MANAGE_EXTERNAL_ACCESS_URL } from '../../config/externalURLs';
import { assign } from 'lodash';

interface Step1Props {
  onNext: (data: any) => void;
  onBack: () => void;
  isGFCMode?: boolean; // Useless variable?
  gfcReady?: boolean;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('24px'),
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    ...shorthands.margin('0', '0', '8px', '0'),
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
  },
  formSection: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
    ...shorthands.padding('24px'),
    backgroundColor: '#ffffff',
    ...shorthands.borderRadius('8px'),
    ...shorthands.border('1px', 'solid', '#e1dfdd'),
  },
  gfcFormSection: {
    ...shorthands.border('1px', 'solid', '#106ebe'),
    backgroundColor: '#f0f8ff',
  },
  helpNote: {
    ...shorthands.margin('8px', '0'),
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    ...shorthands.margin('32px', '0', '0', '0'),
  },
  linkButton: {
    color: '#106ebe',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontSize: '14px',
  },
  gfcSections: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('24px'),
  },
  domainSection: {
    ...shorthands.padding('16px'),
    backgroundColor: '#f8f9fa',
    ...shorthands.borderRadius('8px'),
    ...shorthands.border('1px', 'solid', '#e1dfdd'),
    marginTop: '20px',
  },
  domainHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  domainTitle: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    fontSize: '16px',
    fontWeight: '600',
  },
  domainList: {
    maxHeight: '200px',
    overflowY: 'auto',
    ...shorthands.border('1px', 'solid', '#e1dfdd'),
    ...shorthands.borderRadius('4px'),
    ...shorthands.padding('8px'),
    backgroundColor: '#ffffff',
    marginBottom: '12px',
  },
  domainItem: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.padding('6px'),
    ...shorthands.borderRadius('4px'),
    '&:hover': {
      backgroundColor: '#f0f2f5',
    },
  },
  domainName: {
    flex: 1,
    fontSize: '14px',
    marginLeft: '8px',
  },
  domainOrg: {
    fontSize: '12px',
    color: '#605e5c',
    marginLeft: '8px',
  },
  noDomains: {
    textAlign: 'center',
    ...shorthands.padding('20px'),
    color: '#605e5c',
    fontSize: '14px',
  },
});

const SHOW_INDIVIDUAL_USERS_OPTION = false; // Toggle to show/hide "Individual User" radio option
const ENABLE_ASSIGNMENT_WORKFLOW_ANALYSIS = false; // Toggle to enable/disable "analyzing policy assignments" UI

export const Step1PolicyCreation: React.FC<Step1Props> = ({ 
  onNext, 
  onBack, 
  isGFCMode = false, 
  gfcReady = false 
}) => {
  const styles = useStyles();
  
  // Basic policy fields
  const [policyName, setPolicyName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedEntities, setSelectedEntities] = useState<SelectedEntity[]>([]);
  const [showNoGroupMessage, setShowNoGroupMessage] = useState(false);
  
  // Validation state
  const [existingPolicies, setExistingPolicies] = useState<string[]>([]);
  const [policyNameError, setPolicyNameError] = useState<string>('');
  const [isCreating, setIsCreating] = useState(false);
  
  // Unmanaged policies for selection
  const [unmanagedPolicies, setUnmanagedPolicies] = useState<any[]>([]);
  const [selectedUnmanagedPolicy, setSelectedUnmanagedPolicy] = useState<string>('');
  const [selectedUnmanagedPolicyId, setSelectedUnmanagedPolicyId] = useState<string>('');

  // Warning dialog for policies with user assignments
  const [showUserAssignmentWarning, setShowUserAssignmentWarning] = useState(false);
  const [warningDialogData, setWarningDialogData] = useState<{
    policyName: string;
    userCount: number;
    users: string[];
  } | null>(null);

  // Domain Feed domain selection
  const [approvedDomains, setApprovedDomains] = useState<Domain[]>([]);
  const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
  const [loadingDomains, setLoadingDomains] = useState(false);

  // Parent policy mandatory domains (inherited, non-selectable)
  const [parentPolicyDomains, setParentPolicyDomains] = useState<string[]>([]);

  // Assignment Workflow Analysis (now always triggered on Create Policy)
  const [showWorkflowDialog, setShowWorkflowDialog] = useState(false);
  const [workflowAnalysisData, setWorkflowAnalysisData] = useState<any | null>(null);
  const [loadingWorkflowAnalysis, setLoadingWorkflowAnalysis] = useState(false);
  const [creatingSecurityGroup, setCreatingSecurityGroup] = useState(false);
  const [createdSecurityGroup, setCreatedSecurityGroup] = useState<any | null>(null);
  const [showSecurityGroupSuccessDialog, setShowSecurityGroupSuccessDialog] = useState(false);
  const [securityGroupSuccessData, setSecurityGroupSuccessData] = useState<any | null>(null);
  
  // GFC-specific fields
  const [communicationMode, setCommunicationMode] = useState<CommunicationWithExternalOrgs>('AllowSpecificExternalDomains');
  const [allowedDomains, setAllowedDomains] = useState<string[]>([]);
  const [blockedDomains, setBlockedDomains] = useState<string[]>([]);
  const [riskCategory, setRiskCategory] = useState<'High' | 'Medium' | 'Low' | 'Trusted'>('Low');
  const [complianceRegion, setComplianceRegion] = useState<'EU' | 'US' | 'APAC' | 'Global'>('Global');
  const [selectedExistingPolicy, setSelectedExistingPolicy] = useState<ExistingTeamsPolicy | null>(null);
  const [enableBilateralChat, setEnableBilateralChat] = useState<boolean>(true);
  const [enableFederationAccess, setEnableFederationAccess] = useState<boolean>(true);

  // Load approved domains, default group, and existing policies on component mount
  // Prevent duplicate API calls with proper dependency tracking
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  
  useEffect(() => {
    // Only load once on mount, not on every isGFCMode change
    if (!hasInitialLoad) {
      setHasInitialLoad(true);
      
      // Load data with proper error handling
      const loadInitialData = async () => {
        const promises = [];
        
        if (!isGFCMode) {
          promises.push(loadApprovedDomains());
        }
        promises.push(loadExistingPolicies());
        
        try {
          await Promise.all(promises);
        } catch (error) {
          console.error('Failed to load initial data:', error);
          // Don't block UI - continue with partial data
        }
      };
      
      loadInitialData();
    }
  }, [hasInitialLoad, isGFCMode]);

  // Extract parent policy domains when parent policy is selected
  useEffect(() => {
    if (selectedExistingPolicy) {
      // Handle both raw API format (allowed_domains) and ExistingTeamsPolicy format (allowedDomains)
      const domains = (selectedExistingPolicy as any).allowed_domains || selectedExistingPolicy.allowedDomains || [];
      setParentPolicyDomains(domains);

      const policyName = (selectedExistingPolicy as any).name || selectedExistingPolicy.identity || '';
      console.log(`📋 Parent policy "${policyName}" has ${domains.length} mandatory domains:`, domains);
      console.log('📋 Parent policy data:', selectedExistingPolicy);
    } else {
      setParentPolicyDomains([]);
    }
  }, [selectedExistingPolicy]);

  const eventTracker = useEventTracker();
  const handleActionOnClick = async (elementButton: ElementButton, basedPolicyId: string, assignmentType: string, assignments: string[], domains: string[], policyName?: string, bilateralChat?: string) => {
    await eventTracker.eventTrackOnElementClicked({
      elementName: elementButton,
      pageName: PageName.POLICIES,
      basedPolicyId,
      assignmentType,
      assignments,
      domains,
      ...(policyName && { policyName }),
      ...(bilateralChat && { bilateralChat }),
    });
  };

  const loadApprovedDomains = async () => {
    try {
      setLoadingDomains(true);
      const domains = await domainService.getDomains({ status: 'approved' });
      console.log('Loaded approved domains:', domains);
      setApprovedDomains(domains);

      // ✅ Auto-select all approved domains by default
      const domainNames = domains.map(d => d.domain).filter(Boolean) as string[];
      setSelectedDomains(domainNames);
      console.log(`✅ Auto-selected ${domainNames.length} approved domains by default`);
    } catch (error) {
      console.error('Failed to load approved domains:', error);
      setApprovedDomains([]); // Ensure empty array on error
      setSelectedDomains([]); // Clear selections on error
    } finally {
      setLoadingDomains(false);
    }
  };

  const loadExistingPolicies = async () => {
    try {
      // Get policies from the regular policies endpoint
      const response = await apiClient.get('/policies');
      const data = response.data as any;
      const policies = data.policies || data || [];

      // Debug: Log first policy to see data structure
      if (policies.length > 0) {
        console.log('Sample policy data structure:', JSON.stringify(policies[0], null, 2));
      }

      // Separate managed and unmanaged policies
      const managedPolicies = policies.filter((p: any) =>
        (p.name || p.Identity || '').endsWith('_OD')
      );
      // Get all managed policy names (those ending with _OD)
      const managedPolicyNames = policies
        .filter((p: any) => (p.name || p.Identity || '').endsWith('_OD'))
        .map((p: any) => (p.name || p.Identity || '').replace(/_OD$/, '').toLowerCase());
      
      // Filter policies ready for onboarding using backend business logic
      // Backend now provides readyForOnboarding flag and onboardingBlockers array
      const unmanagedPoliciesList = policies.filter((p: any) => {
        const policyName = p.name || p.Identity || '';

        // Exclude managed policies (ending with _OD)
        if (policyName.endsWith('_OD')) return false;

        // Exclude if a managed version already exists
        if (managedPolicyNames.includes(policyName.toLowerCase())) return false;

        // Use backend's federation readiness logic
        if (p.readyForOnboarding === true) {
          console.log(`✅ Policy ${policyName} READY: Backend confirmed ready for onboarding`);
          return true;
        } else if (p.readyForOnboarding === false) {
          const blockers = p.onboardingBlockers || ['Unknown reason'];
          console.log(`❌ Policy ${policyName} NOT READY: ${blockers.join(', ')}`);
          return false;
        }

        // Fallback: if readyForOnboarding not present, exclude for safety
        console.log(`⚠️ Policy ${policyName} EXCLUDED: Backend readyForOnboarding status unknown`);
        return false;
      });
      
      // Sort unmanaged policies alphabetically
      unmanagedPoliciesList.sort((a: any, b: any) => {
        const nameA = (a.name || a.Identity || '').toLowerCase();
        const nameB = (b.name || b.Identity || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      
      // Store unmanaged policies for dropdown
      setUnmanagedPolicies(unmanagedPoliciesList);
      console.log(`📋 Found ${unmanagedPoliciesList.length} policies ready for onboarding (verified by backend business logic)`);

      // Extract all policy names for validation
      const policyNames = policies.map((p: any) => p.name || p.Identity || '').filter(Boolean);
      setExistingPolicies(policyNames);
      console.log('Loaded all existing policies:', policyNames);
      console.log('🎯 Enterprise filtering result - Policies ready for onboarding:',
                  unmanagedPoliciesList.map((p: any) => `${p.name || p.Identity} (${p.federationConfig?.communicationWithExternalOrgs || 'Unknown config'})`));
    } catch (error) {
      console.error('Failed to load existing policies:', error);
      // Try alternate method using GFC service
      try {
        const gfcData = await gfcPolicyService.getExistingTeamsPolicies();
        const policyNames = gfcData.policies.map((p: any) => p.identity || p.name || '').filter(Boolean);
        setExistingPolicies(policyNames);
        console.log('Loaded existing policies from GFC service:', policyNames);
      } catch (gfcError) {
        console.error('Failed to load policies from GFC service:', gfcError);
        setExistingPolicies([]);
      }
    }
  };

  // Validate policy name for duplicates (case-insensitive)
  const validatePolicyName = (name: string) => {
    if (!name.trim()) {
      setPolicyNameError('Policy name is required');
      return false;
    }

    // Add _OD suffix for the check
    const fullPolicyName = `${name.trim()}_OD`;
    
    // Check for duplicate (case-insensitive)
    const isDuplicate = existingPolicies.some(
      existing => existing.toLowerCase() === fullPolicyName.toLowerCase()
    );

    if (isDuplicate) {
      setPolicyNameError(`A policy named "${fullPolicyName}" already exists. Please choose a different name.`);
      return false;
    }

    setPolicyNameError('');
    return true;
  };

  // Handle policy name change with validation
  const handlePolicyNameChange = (value: string) => {
    setPolicyName(value);
    if (value) {
      validatePolicyName(value);
    } else {
      setPolicyNameError('');
    }
  };
  
  // Handle unmanaged policy selection from dropdown
  const handleUnmanagedPolicySelect = (event: any, data: any) => {
    // For Fluent UI v9 Dropdown, the selected value is in data.optionValue
    const selectedPolicy = data.optionValue || '';
    console.log('Selected unmanaged policy:', selectedPolicy, 'Full data:', data);

    setSelectedUnmanagedPolicy(selectedPolicy);

    // Remove _OD suffix if it exists (shouldn't happen with unmanaged, but just in case)
    const cleanPolicyName = selectedPolicy.replace(/_OD$/, '');
    setPolicyName(cleanPolicyName);

    // Validate the new name
    if (cleanPolicyName) {
      validatePolicyName(cleanPolicyName);
    }
  };

  // Fetch assignments when an unmanaged policy is selected
  useEffect(() => {
    if (!selectedUnmanagedPolicy) {
      console.log('No unmanaged policy selected, reloading default group');
      // When no policy is selected, restore default group
      setSelectedExistingPolicy(null);
      return;
    }

  // Helper function to fetch policy data from API
  const fetchPolicyData = async (policyName: string) => {
    const response = await apiClient.get(`/policies/by-name/${encodeURIComponent(policyName)}`);
    console.log('API Response:', response);
    return (response as any).data?.data || (response as any).data;
  };

  // Helper function to process group assignments
  const processGroupAssignments = (assignments: any): SelectedEntity[] => {
    const entities: SelectedEntity[] = [];
    
    if (assignments.groups && Array.isArray(assignments.groups)) {
      assignments.groups.forEach((group: any) => {
        console.log('Processing group:', group);
        if (group.groupId && group.displayName) {
          entities.push({
            id: group.groupId,
            displayName: group.displayName,
            type: 'group',
            subType: 'Security' // Default to Security for groups
          });
        }
      });
    }
    
    return entities;
  };

  // Helper function to find existing user in entities array
  const findExistingUser = (entities: SelectedEntity[], user: any): SelectedEntity | undefined => {
    return entities.find(entity => 
      entity.type === 'user' && 
      (entity.id === user.userId || entity.displayName === user.displayName)
    );
  };

  // Helper function to process user assignments
  const processUserAssignments = (assignments: any, existingEntities: SelectedEntity[]): SelectedEntity[] => {
    const entities: SelectedEntity[] = [...existingEntities];
    
    if (assignments.users && Array.isArray(assignments.users)) {
      assignments.users.forEach((user: any) => {
        console.log('Processing user:', user);
        if (user.userId && (user.displayName || user.userPrincipalName)) {
          // Check if user is already in entities (avoid duplicates)
          const existingUser = findExistingUser(entities, user);

          if (!existingUser) {
            // Use actual display name if available, otherwise extract from email
            let userDisplayName = user.displayName;

            // If displayName is missing or is the same as email, extract username
            if (!userDisplayName || userDisplayName === user.userPrincipalName) {
              // Extract username part before @ from email
              userDisplayName = user.userPrincipalName?.split('@')[0] || user.userId;
            }

            entities.push({
              id: user.userId,
              displayName: userDisplayName,
              type: 'user',
              subType: 'user'
              // Note: Full email is in user.userId if needed
            });
          } else {
            console.log(`Skipping duplicate user: ${user.displayName || user.userPrincipalName}`);
          }
        }
      });
    }
    
    return entities;
  };

  // Helper function to ensure default group is included
  const ensureDefaultGroupIncluded = (entities: SelectedEntity[], defaultGroupEntity: SelectedEntity | null): SelectedEntity[] => {
    if (defaultGroupEntity !== null) {
      const defaultGroup = defaultGroupEntity; // TypeScript type narrowing
      const isAlreadyIncluded = entities.find(e => e.id === defaultGroup.id);
      if (!isAlreadyIncluded) {
        // Add default group at the beginning
        entities.unshift(defaultGroup);
        console.log(`✅ Added default group "${defaultGroup.displayName}" to assignments`);
      }
    }
    return entities;
  };

  const handleFetchPolicyAssignmentsError = async (error: any) => {
    // Policy might not be in database yet (not synced)
    // This is not an error condition - just means no assignments to inherit
    if (error?.response?.status === 404) {
      console.log(`Policy ${selectedUnmanagedPolicy} not found in database - may need to sync policies first`);
    }
  }

  // Main function to fetch policy assignments
  const fetchPolicyAssignments = async () => {
    try {
      console.log(`🔄 Fetching assignments for unmanaged policy: ${selectedUnmanagedPolicy}`);

      // Clear existing assignments before fetching new ones
      setSelectedEntities([]);

      // Call the API to get policy details by name
      const policyData = await fetchPolicyData(selectedUnmanagedPolicy);
      console.log('Policy Data:', policyData);

      if (!policyData) {
        console.warn(`No data found for policy: ${selectedUnmanagedPolicy}`);
        setSelectedExistingPolicy(null);
        return;
      }

      // Store policy ID
      if (policyData.id) {
        setSelectedUnmanagedPolicyId(policyData.id);
      }

      // Set the full policy data to selectedExistingPolicy so domains and rank can be extracted
      setSelectedExistingPolicy(policyData as any);

      // Extract assignments from the response
      const assignments = policyData.assignments;
      console.log('Raw assignments data:', assignments);

      // Check if policy has user assignments and show warning
      if (assignments && assignments.users && assignments.users.length > 0) {
        const userNames = assignments.users.map((u: any) =>
          u.user_display_name || u.displayName || u.user_principal_name || 'Unknown User'
        );

        setWarningDialogData({
          policyName: selectedUnmanagedPolicy,
          userCount: assignments.users.length,
          users: userNames.slice(0, 5) // Show first 5 users
        });
        setShowUserAssignmentWarning(true);
      }

      if (!assignments || ((!assignments.groups || assignments.groups.length === 0) && (!assignments.users || assignments.users.length === 0))) {
        console.log(`No assignments found for policy: ${selectedUnmanagedPolicy}, adding default group`);
        // Add default group if no assignments found
        return;
      }

      console.log(`📊 Found assignments for ${selectedUnmanagedPolicy}:`, {
        groupCount: assignments.groups?.length || 0,
        userCount: assignments.users?.length || 0,
        groups: assignments.groups,
        users: assignments.users
      });

      // Process assignments into SelectedEntity format
      let entities = processGroupAssignments(assignments);
      entities = processUserAssignments(assignments, entities);

      // Always ensure default group is included
      entities = ensureDefaultGroupIncluded(entities, null);

      console.log(`✅ Populating ${entities.length} assignments for ${selectedUnmanagedPolicy} (including default group):`, entities);
      setSelectedEntities(entities);

    } catch (error: any) {
      // Log detailed error information
      console.error(`Error fetching assignments for ${selectedUnmanagedPolicy}:`, {
        message: error?.message,
        response: error?.response,
        status: error?.response?.status,
        data: error?.response?.data
      });

      handleFetchPolicyAssignmentsError(error);
    }
  };

    fetchPolicyAssignments();
  }, [selectedUnmanagedPolicy]); // Re-run when selectedUnmanagedPolicy changes

  const handleDomainToggle = (domain: string) => {
    setSelectedDomains(prev => {
      if (prev.includes(domain)) {
        return prev.filter(d => d !== domain);
      } else {
        return [...prev, domain];
      }
    });
  };

  const handleSelectAllDomains = () => {
    if (selectedDomains.length === approvedDomains.length) {
      setSelectedDomains([]);
    } else {
      setSelectedDomains(approvedDomains.map(d => d.domain).filter(Boolean) as string[]);
    }
  };

  const handleAnalyzeAssignments = async () => {
    console.log('log log marc');
    if (!selectedUnmanagedPolicy) {
      console.error('No policy selected for analysis');
      return;
    }

    setLoadingWorkflowAnalysis(true);
    try {
      // Call the backend API to analyze the policy assignments
      const response = await apiClient.post('/policies/analyze-assignments', {
        policyName: selectedUnmanagedPolicy
      });

      setWorkflowAnalysisData(response.data);
      setShowWorkflowDialog(true);
    } catch (error) {
      console.error('Failed to analyze policy assignments:', error);
      alert('Failed to analyze policy assignments. Please try again.');
    } finally {
      setLoadingWorkflowAnalysis(false);
    }
  };

  const handleCreateSecurityGroupAndApply = async () => {
    if (!selectedUnmanagedPolicy || !workflowAnalysisData) {
      console.error('No policy or analysis data available');
      return;
    }

    setCreatingSecurityGroup(true);
    try {
      // Call the backend API to create the security group
      const response = await apiClient.post('/policies/create-assignment-security-group', {
        policyName: selectedUnmanagedPolicy,
        analysisData: workflowAnalysisData.data || workflowAnalysisData
      });

      const createdGroup = (response as any).data?.data || (response as any).data;
      console.log('✅ Security group created:', createdGroup);
      setCreatedSecurityGroup(createdGroup);

      // Create the new group entity that will be assigned to the policy
      const newGroupEntity: SelectedEntity = {
        id: createdGroup.groupId,
        displayName: createdGroup.displayName,
        type: 'group',
        subType: 'Security'
      };

      console.log('📋 New group entity for policy assignment:', newGroupEntity);

      // Update selectedEntities with the newly created security group
      setSelectedEntities([newGroupEntity]);

      // Close the workflow dialog
      setShowWorkflowDialog(false);

      // Show success dialog with formatted data
      setSecurityGroupSuccessData({
        displayName: createdGroup.displayName,
        membersAdded: createdGroup.membersAdded,
        members: createdGroup.members || []
      });
      setShowSecurityGroupSuccessDialog(true);

      // Automatically proceed with policy creation after security group is created
      // Pass the entities directly instead of relying on state (which may not be updated yet)
      console.log('✅ Security group created successfully, proceeding to create policy with new group...');
      await proceedWithPolicyCreationWithEntities([newGroupEntity]);

    } catch (error: any) {
      console.error('Failed to create security group:', error);
      alert(`Failed to create security group: ${error.response?.data?.error || error.message || 'Unknown error'}`);
    } finally {
      setCreatingSecurityGroup(false);
    }
  };

  const handleUseIndividualAssignments = async () => {
    console.log('🚀 [OPTION B] handleUseIndividualAssignments called');
    console.log('📋 Selected unmanaged policy:', selectedUnmanagedPolicy);
    console.log('📊 Workflow analysis data:', workflowAnalysisData);

    if (!selectedUnmanagedPolicy || !workflowAnalysisData) {
      console.error('❌ [OPTION B] CRITICAL: No policy or analysis data available');
      console.error('  selectedUnmanagedPolicy:', selectedUnmanagedPolicy);
      console.error('  workflowAnalysisData:', workflowAnalysisData);
      alert('CRITICAL ERROR: Missing policy or analysis data. Cannot proceed with Option B.');
      return;
    }

    try {
      console.log('🔄 [OPTION B] Using individual user assignments from parent policy...');

      // CRITICAL FIX: Use the ALREADY LOADED selectedEntities that was populated
      // when the parent policy was selected. Don't rebuild from workflowAnalysisData
      // because the data structures may differ.

      console.log('📊 Current selectedEntities state:', selectedEntities);
      console.log(`  - Total entities: ${selectedEntities.length}`);
      console.log(`  - Groups: ${selectedEntities.filter(e => e.type === 'group').length}`);
      console.log(`  - Users: ${selectedEntities.filter(e => e.type === 'user').length}`);

      // Just use the existing entities - they already have the correct structure
      const entities = [...selectedEntities];

      // If selectedEntities was empty (edge case), fall back to workflowAnalysisData
      if (entities.length === 0) {
        console.warn('⚠️ selectedEntities was empty, falling back to workflowAnalysisData');
        const parentUsers = workflowAnalysisData.parentPolicyAssignments?.users || [];
        console.log(`📊 Fallback: Parent policy has ${parentUsers.length} individual user assignments from analysis`);

        parentUsers.forEach((user: any, index: number) => {
          // Handle both camelCase and snake_case field names (database returns snake_case)
          const userId = user.userId || user.user_id || user.id;
          const userEmail = user.userEmail || user.user_principal_name || user.userPrincipalName || user.email || user.mail;
          let userDisplayName = user.displayName || user.user_display_name || user.userDisplayName;

          // If displayName is missing or is the same as email, extract username from email
          if (!userDisplayName || userDisplayName === userEmail) {
            userDisplayName = userEmail?.split('@')[0] || userId;
          }

          console.log(`  👤 User ${index + 1}: ${userDisplayName} (${userEmail}) [ID: ${userId}]`);

          if (userId && userDisplayName && userEmail) {
            entities.push({
              id: userId,
              displayName: userDisplayName,
              type: 'user',
              subType: 'user',
              userPrincipalName: userEmail,  // Required by backend
              mail: userEmail  // Alternate field for compatibility
            });
            console.log(`    ✅ Added to entities list with email: ${userEmail}`);
          } else {
            console.error(`    ❌ Skipping user - missing required fields (userId: ${userId}, displayName: ${userDisplayName}, email: ${userEmail})`);
          }
        });
      } else {
        console.log('✅ Using already-loaded entities from selectedEntities state (no need to rebuild)');
      }

      const groupCount = entities.filter(e => e.type === 'group').length;
      const userCount = entities.filter(e => e.type === 'user').length;

      console.log(`✅ [OPTION B] Final assignment list: ${entities.length} total entities`);
      console.log(`  - Groups: ${groupCount}`);
      console.log(`  - Users: ${userCount}`);

      // CRITICAL VALIDATION: Make sure we have at least some assignments
      if (entities.length === 0) {
        const errorMsg = 'CRITICAL VALIDATION FAILURE: No entities to assign! Cannot create policy with zero assignments.';
        console.error(`❌ [OPTION B] ${errorMsg}`);
        alert(errorMsg);
        return;
      }

      // Log what we're about to assign
      console.log('📋 [OPTION B] Entities that will be assigned:');
      entities.forEach((e, i) => {
        if (e.type === 'group') {
          console.log(`  ${i + 1}. GROUP: ${e.displayName} (${e.id})`);
        } else {
          console.log(`  ${i + 1}. USER: ${e.displayName} (${e.userPrincipalName || e.mail || e.id})`);
        }
      });

      // Update selectedEntities with the combined list
      setSelectedEntities(entities);
      console.log('✅ [OPTION B] Updated selectedEntities state');
      
      // Close the workflow dialog
      setShowWorkflowDialog(false);

      // Proceed directly to policy creation with the selected entities
      console.log('✅ [OPTION B] Proceeding to create policy with individual user assignments...');
      console.log('📋 [OPTION B] Entities to assign:', JSON.stringify(entities, null, 2));
      console.log(`📊 [OPTION B] Summary: ${entities.length} total entities`);
      console.log(`   - Groups: ${entities.filter(e => e.type === 'group').length}`);
      console.log(`   - Users: ${entities.filter(e => e.type === 'user').length}`);

      // Trigger the handleNext flow to create the policy
      // We need to call the policy creation logic directly
      console.log('🚀 [OPTION B] Calling proceedWithPolicyCreationWithEntities...');
      await proceedWithPolicyCreationWithEntities(entities);

    } catch (error: any) {
      console.error('Failed to use individual assignments:', error);
      alert(`Failed to set up individual assignments: ${error.message || 'Unknown error'}`);
    }
  };

  // Policy creation function that accepts entities as a parameter
  // This avoids issues with stale state when calling immediately after setState
  const proceedWithPolicyCreationWithEntities = async (entities: SelectedEntity[]) => {
    // This is the policy creation logic extracted from handleNext
    // Set creating flag to prevent double-submission
    setIsCreating(true);

    try {
      // UUID validation helper
      const isValidUUID = (uuid: string) => {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        return uuidRegex.test(uuid);
      };

      // Validate all entity IDs are proper UUIDs
      const invalidEntities = entities.filter(entity => !isValidUUID(entity.id));
      if (invalidEntities.length > 0) {
        const errorMsg = `Invalid entity ID format detected. Expected UUID but got: ${invalidEntities.map(e => `${e.displayName} (${e.id})`).join(', ')}`;
        console.error('Invalid entity IDs detected:', invalidEntities);
        setPolicyNameError(errorMsg);
        alert(errorMsg);
        setIsCreating(false);
        return;
      }

      console.log('📤 Creating policy with entities:', entities);

      // Standard mode data structure
      const formData = {
        policyName,
        syncWithOrg: 'Y',
        selectedEntities: entities.map(entity => ({
          id: entity.id,
          displayName: entity.displayName,
          type: entity.type,
          subType: entity.subType,
          userPrincipalName: (entity as any).userPrincipalName,  // Include email for users
          mail: (entity as any).mail  // Include alternate email field
        })),
        entraGroupId: entities.length > 0 ? entities[0].id : undefined,
        bilateralChat: true,
        enableFederationAccess,
        includedDomains: Array.from(new Set([...parentPolicyDomains, ...selectedDomains])),
      };

      console.log('📤 Form data being sent to backend:');
      console.log('  Policy Name:', formData.policyName);
      console.log('  Entities Count:', formData.selectedEntities.length);
      console.log('  Groups:', formData.selectedEntities.filter(e => e.type === 'group').map(e => `${e.displayName} (${e.id})`));
      console.log('  Users:', formData.selectedEntities.filter(e => e.type === 'user').map(e => `${e.displayName} - ${(e as any).userPrincipalName || (e as any).mail || e.id}`));
      console.log('  Full formData:', JSON.stringify(formData, null, 2));

      await onNext(formData);
    } catch (error) {
      console.error('Failed to create policy:', error);
      setIsCreating(false);
    } finally {
      setIsCreating(false);
    }
  };

  // Helper function to validate entity UUIDs
  const validateEntityUUIDs = (entities: SelectedEntity[]): string | null => {
    const isValidUUID = (uuid: string) => {
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      return uuidRegex.test(uuid);
    };

    const invalidEntities = entities.filter(entity => !isValidUUID(entity.id));
    if (invalidEntities.length > 0) {
      return 'Invalid entity ID format detected. Expected UUID but got: ' + invalidEntities.map(e => `${e.displayName} (${e.id})`).join(', ');
    }
    return null;
  };

  // Helper function to create GFC mode form data
  const createGFCFormData = () => {
    return {
      policyName,
      displayName,
      description,
      entraGroupId: selectedEntities.length > 0 ? selectedEntities[0].id : undefined,  // ADM-001: First entity ID for backward compat
      gfcOptions: {
        communicationMode,
        allowedDomains,
        blockedDomains,
        riskCategory,
        complianceRegion,
        selectedExistingPolicy,
      },
    };
  };

  // Helper function to create standard mode form data
  const createStandardFormData = () => {
    return {
      policyName,
      syncWithOrg: 'Y',  // Always sync with org
      selectedEntities: selectedEntities.map(entity => ({
        id: entity.id,
        displayName: entity.displayName,
        type: entity.type,
        subType: entity.subType
      })),
      entraGroupId: selectedEntities.length > 0 ? selectedEntities[0].id : undefined,  // ADM-001: First entity ID for backward compat
      bilateralChat: enableBilateralChat,
      enableFederationAccess,
      // Merge parent policy domains (mandatory) + selected domains (optional), deduplicate
      includedDomains: Array.from(new Set([...parentPolicyDomains, ...selectedDomains])),
    };
  };

  // Helper function to handle GFC mode policy creation
  const handleGFCModeCreation = async () => {
    const formData = createGFCFormData();
    try {
      await onNext(formData);
    } catch (error) {
      console.error('Failed to create GFC policy:', error);
    } finally {
      setIsCreating(false);
    }
  };

  // Helper function to handle standard mode policy creation
  const handleStandardModeCreation = async () => {
    // Validate all entity IDs are proper UUIDs
    const validationError = validateEntityUUIDs(selectedEntities);
    if (validationError) {
      console.error('Invalid entity IDs detected:', selectedEntities);
      setPolicyNameError(validationError);
      alert(validationError); // Also show an alert for immediate feedback
      setIsCreating(false);
      return;
    }

    const formData = createStandardFormData();
    try {
      await onNext(formData);
    } catch (error) {
      console.error('Failed to create policy:', error);
      // Re-enable button if creation fails
      setIsCreating(false);
    } finally {
      // Always reset flag after attempt
      setIsCreating(false);
    }
  };

  const handleNext = async () => {
    // Prevent double-clicks
    if (isCreating) {
      console.warn('Policy creation already in progress');
      return;
    }

    // Validate policy name before proceeding
    if (!validatePolicyName(policyName)) {
      console.error('Policy validation failed:', policyNameError);
      return; // Don't proceed if validation fails
    }

    // NEW FLOW: Automatically trigger analysis before creating policy
    // This shows the dialog with assignment options
    if (!isGFCMode && selectedUnmanagedPolicy && ENABLE_ASSIGNMENT_WORKFLOW_ANALYSIS) {
      console.log('🔄 Automatically analyzing assignments before policy creation...');
      await handleAnalyzeAssignments();
      // Note: Policy creation will be triggered from the dialog buttons
      return; // Don't proceed with creation yet - wait for user choice in dialog
    }

    // Set creating flag to prevent double-submission
    setIsCreating(true);

    if (isGFCMode) {
      await handleGFCModeCreation();
    } else {
      await handleStandardModeCreation();
    }
  };

  const handleEntityChange = (entities: SelectedEntity[]) => {
    console.log('🔄 Entity selection changed:', entities.map(e => `${e.displayName} (${e.type})`));
    setSelectedEntities(entities);
    setShowNoGroupMessage(false);

    // Enhanced validation logging
    const groups = entities.filter(e => e.type === 'group');
    const users = entities.filter(e => e.type === 'user');
    console.log(`📊 Entity breakdown: ${groups.length} groups, ${users.length} users`);
  };

  const handleNoGroupClick = () => {
    // Don't clear the group, just show the help message
    setShowNoGroupMessage(true);
  };

  const getTotalDomains = () => allowedDomains.length + blockedDomains.length;
  // VALIDATION LOGIC - Group/User selection is REQUIRED (never optional)
  // GFC mode allows proceeding without group selection, but non-GFC mode requires group selection
  // Must select an unmanaged policy from the dropdown
  const canProceed = selectedUnmanagedPolicy && policyName.trim() && !policyNameError && (isGFCMode ? true : selectedEntities.length > 0);

  return (
    <div className={styles.container} data-testid="wizard-step1-policy-creation">
      <div className={styles.title} data-testid="wizard-step1-policy-creation-title">
        {isGFCMode ? <Sparkle24Regular style={{ color: '#106ebe' }} /> : <Shield24Regular />}
        <Text data-testid="wizard-step1-policy-creation-title-text">
          Create a new Teams External Access policy
        </Text>
        {isGFCMode && <Badge appearance="filled" color="brand" data-testid="wizard-step1-policy-creation-enhanced-badge">ENHANCED</Badge>}
      </div>
      
      {/* Basic Policy Configuration */}
      <div className={mergeClasses(styles.formSection, isGFCMode && styles.gfcFormSection)} data-testid="wizard-step1-policy-creation-form-section">
        <Field
          label="Select Base Policy (Ready to Onboard):"
          required
          validationState={policyNameError ? "error" : "none"}
          validationMessage={policyNameError}
          hint={policyName && !policyNameError ? `Will be created as: ${policyName}_OD` : undefined}
          data-testid="wizard-step1-policy-creation-base-policy-field"
        >
          <Dropdown
            value={selectedUnmanagedPolicy}
            selectedOptions={selectedUnmanagedPolicy ? [selectedUnmanagedPolicy] : []}
            onOptionSelect={handleUnmanagedPolicySelect}
            placeholder={unmanagedPolicies.length > 0 ?
              "Select a policy with federation configured" :
              "No policies ready to onboard - configure federation first"}
            style={{ width: '100%' }}
            disabled={unmanagedPolicies.length === 0}
            data-testid="wizard-step1-policy-creation-base-policy-dropdown"
          >
            {unmanagedPolicies.map((policy: any, index: number) => {
              const policyIdentity = policy.name || policy.Identity || '';
              return (
                <Option key={policyIdentity} value={policyIdentity} text={policyIdentity} data-testid={`wizard-step1-policy-creation-base-policy-option-${index}`}>
                  {policyIdentity}
                </Option>
              );
            })}
          </Dropdown>
        </Field>

        {/* Display parent policy rank when a policy is selected */}
        {!isGFCMode && selectedExistingPolicy && (() => {
          // The API returns: { data: { assignments: { groups: [{rank, groupId, displayName}] } } }
          const assignments = (selectedExistingPolicy as any)?.assignments;
          const groups = assignments?.groups || [];

          if (groups.length > 0 && groups[0].rank !== null && groups[0].rank !== undefined) {
            const rank = groups[0].rank;
            return (
              <div style={{
                padding: '12px',
                backgroundColor: '#f0f8ff',
                borderRadius: '4px',
                border: '1px solid #0078d4',
                marginTop: '8px'
              }} data-testid="wizard-step1-policy-creation-div">
                <Text size={300} style={{ color: '#0078d4' }} data-testid="wizard-step1-policy-creation-parent-rank-text">
                  📊 <strong data-testid="wizard-step1-policy-creation-strong">Parent Policy Rank:</strong> {rank}
                </Text>
                <Text size={200} style={{ color: '#605e5c', display: 'block', marginTop: '4px' }} data-testid="wizard-step1-policy-creation-parent-rank-help-text">
                  The managed policy will inherit this same rank/priority
                </Text>
              </div>
            );
          }
          return null;
        })()}

        {/* Removed: Assignment Workflow Analysis Checkbox - Now triggered automatically on Create Policy */}

        {isGFCMode && (
          <>
            <Field label="Display Name:" data-testid="wizard-step1-policy-creation-display-name-field">
              <Input
                value={displayName}
                onChange={(e, data) => setDisplayName(data.value)}
                placeholder="Human-readable policy name (optional)"
                data-testid="wizard-step1-policy-creation-display-name-input"
              />
            </Field>

            <Field label="Description:" data-testid="wizard-step1-policy-creation-description-field">
              <Textarea
                value={description}
                onChange={(e: any, data: any) => setDescription(data.value)}
                placeholder="Policy description for documentation and compliance (optional)"
                rows={3}
                data-testid="wizard-step1-policy-creation-description-textarea"
              />
            </Field>
          </>
        )}

        <Field label={isGFCMode ? "Assign to Users/Groups (Optional):" : "Assign policy to groups:"} required={!isGFCMode} data-testid="wizard-step1-policy-creation-assignment-field">
          <UserGroupSelectorWithRadio
            value={selectedEntities}
            onChange={handleEntityChange}
            disabled={false}
            multiple={true}
            required={!isGFCMode}
            showIndividualUsersOption={SHOW_INDIVIDUAL_USERS_OPTION}
          />
          {!isGFCMode && selectedEntities.length === 0 && (
            <div style={{ 
              marginTop: '8px', 
              padding: '8px 12px', 
              backgroundColor: '#fff4ce', 
              borderLeft: '4px solid #ffc107',
              borderRadius: '4px',
              fontSize: '13px',
              color: '#333'
            }} data-testid="wizard-step1-policy-creation-div-2">
              <strong data-testid="wizard-step1-policy-creation-strong-2">Important:</strong> {SHOW_INDIVIDUAL_USERS_OPTION ?
              'Policy creation requires at least one user or group assignment for policy effectiveness. You can select multiple users and/or groups. Policies without assignments are created but have no effect.'
              : 'Policy creation requires at least one group assignment for policy effectiveness. Policies without assignments are created but have no effect.'}
            </div>
          )}
          <Button 
            appearance="transparent" 
            size="small"
            onClick={handleNoGroupClick}
            style={{ 
              color: '#106ebe', 
              textDecoration: 'underline', 
              padding: '4px 0',
              minWidth: 'auto',
              fontSize: '14px'
            }}
            data-testid="wizard-step1-policy-creation-no-group-help-button"
          >
            I don't have one - why is this needed?
          </Button>
          {showNoGroupMessage && (
            <MessageBar intent="warning" style={{ marginTop: '8px' }} data-testid="wizard-step1-policy-creation-no-group-warning-bar">
              <MessageBarBody data-testid="wizard-step1-policy-creation-no-group-warning-body">
                <Text style={{ fontSize: '13px' }} data-testid="wizard-step1-policy-creation-no-group-warning-text">
                  <strong data-testid="wizard-step1-policy-creation-strong-3">User or group assignment is required.</strong> Teams federation policies must be assigned to specific users or groups. 
                  Without assignments, the policy won't apply to anyone and external collaboration won't work.
                  <br data-testid="wizard-step1-policy-creation-br" /><br data-testid="wizard-step1-policy-creation-br-2" />
                  You can:
                  <br data-testid="wizard-step1-policy-creation-br-3" />• Search for and select individual users by name or email
                  <br data-testid="wizard-step1-policy-creation-br-4" />• Search for and select Entra ID security groups
                  <br data-testid="wizard-step1-policy-creation-br-5" />• Select multiple users and/or groups for this policy
                  <br data-testid="wizard-step1-policy-creation-br-6" />• Create a new security group in Azure Portal → Entra ID → Groups if needed
                </Text>
              </MessageBarBody>
            </MessageBar>
          )}
        </Field>

        <Field label="Enable bilateral chat (limit new external group chats to two organisations)" data-testid="wizard-step1-policy-creation-bilateral-chat-field">
          <Switch checked={enableBilateralChat} onChange={(e, data) => setEnableBilateralChat(data.checked)} label={{ children: enableBilateralChat ? 'On' : 'Off', style: { color: '#535353' } }} data-testid="wizard-step1-policy-creation-bilateral-chat-switch" />
          <Text style={{ fontSize: '14px', marginTop: '4px', color: '#535353' }} data-testid="wizard-step1-policy-creation-bilateral-chat-description">
            Limits new group chats with Teams and Skype for Business users from trusted organisations to two organisations — yours plus one external. Existing external group chats with more than two organisations are disabled for users and groups assigned to this policy.
          </Text>
        </Field>

        <Divider data-testid="wizard-step1-policy-creation-settings-divider" />

        <Field label="Enable federation access" data-testid="wizard-step1-policy-creation-federation-access-field">
          <Switch
            checked={enableFederationAccess}
            onChange={(e, data) => setEnableFederationAccess(data.checked)}
            disabled
            label={{ children: enableFederationAccess ? 'On' : 'Off', style: { color: '#535353' } }}
            data-testid="wizard-step1-policy-creation-federation-access-switch"
          />
          <Text style={{ fontSize: '14px', marginTop: '4px', color: '#535353' }} data-testid="wizard-step1-policy-creation-federation-access-description">
            Allows users to chat and meet with people in other Teams organisations and Skype for Business. Read{' '}
            <Link href={MANAGE_EXTERNAL_ACCESS_URL} target="_blank" rel="noopener noreferrer" data-testid="wizard-step1-policy-creation-federation-access-link">
              Manage external access in Microsoft Teams
            </Link>{' '}
            for more information.
          </Text>
        </Field>

        {/* Domain Feed Domain Selection for Standard Mode */}
        {!isGFCMode && (
          <div className={styles.domainSection} data-testid="wizard-step1-policy-creation-domain-section">
            <div className={styles.domainHeader} data-testid="wizard-step1-policy-creation-domain-header">
              <div className={styles.domainTitle} data-testid="wizard-step1-policy-creation-domain-title">
                <Globe24Regular />
                <Text data-testid="wizard-step1-policy-creation-select-approved-domains-text">Select Approved Domains</Text>
              </div>
              <Badge appearance="filled" color="brand" data-testid="wizard-step1-policy-creation-selected-count-badge">
                {selectedDomains.length} selected
              </Badge>
            </div>
            
            {loadingDomains ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }} data-testid="wizard-step1-policy-creation-div-3">
                <Spinner label="Loading approved domains..." data-testid="wizard-step1-policy-creation-loading-domains-spinner" />
              </div>
            ) : approvedDomains.length > 0 ? (
              <>
                {/* Parent Policy Mandatory Domains (if any) */}
                {parentPolicyDomains.length > 0 && (
                  <div style={{ marginBottom: '16px' }} data-testid="wizard-step1-policy-creation-div-4">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }} data-testid="wizard-step1-policy-creation-div-5">
                      <Text weight="semibold" size={300} data-testid="wizard-step1-policy-creation-mandatory-domains-title">
                        📋 Mandatory Domains (inherited from parent policy)
                      </Text>
                      {(() => {
                        // Handle both raw API format (groups) and ExistingTeamsPolicy format (assignedGroups)
                        const groups = (selectedExistingPolicy as any)?.groups || selectedExistingPolicy?.assignedGroups || [];
                        if (groups.length > 0) {
                          const rank = groups[0].rank || 'N/A';
                          return (
                            <Text size={200} style={{ color: '#605e5c' }} data-testid="wizard-step1-policy-creation-parent-rank-inline-text">
                              Parent Rank: {rank}
                            </Text>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <div className={styles.domainList} data-testid="wizard-step1-policy-creation-domain-list">
                      {parentPolicyDomains.map((domainName, index) => (
                        <div key={`parent-${domainName}`} className={styles.domainItem} data-testid={`wizard-step1-policy-creation-domain-item-${index}`}>
                          <Checkbox
                            checked={true}
                            disabled={true}
                            title="This domain is mandatory (inherited from parent policy)"
                            data-testid={`wizard-step1-policy-creation-mandatory-domain-checkbox-${index}`}
                          />
                          <Text className={styles.domainName} style={{ opacity: 0.7 }} data-testid={`wizard-step1-policy-creation-mandatory-domain-name-${index}`}>{domainName}</Text>
                          <Badge appearance="filled" color="informative" size="small" data-testid={`wizard-step1-policy-creation-mandatory-domain-badge-${index}`}>
                            Mandatory
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Approved Domains (optional selections) */}
                <div style={{ marginTop: parentPolicyDomains.length > 0 ? '16px' : '0' }} data-testid="wizard-step1-policy-creation-div-6">
                  {parentPolicyDomains.length > 0 && (
                    <Text weight="semibold" size={300} style={{ marginBottom: '8px', display: 'block' }} data-testid="wizard-step1-policy-creation-additional-domains-title">
                      ➕ Additional Domains (optional)
                    </Text>
                  )}
                  <Button
                    appearance="subtle"
                    size="small"
                    onClick={handleSelectAllDomains}
                    style={{ marginBottom: '8px' }}
                    data-testid="wizard-step1-policy-creation-select-all-domains-button"
                  >
                    {selectedDomains.length === approvedDomains.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
                <div className={styles.domainList} data-testid="wizard-step1-policy-creation-domain-list-2">
                  {approvedDomains
                    .filter(domain => !parentPolicyDomains.some((d) => d.toLowerCase() === domain.domain.toLowerCase()))
                    .map((domain, index) => {
                      const domainName = domain.domain || '';
                      return (
                        <div key={domain.domain} className={styles.domainItem} data-testid={`wizard-step1-policy-creation-domain-item-2-${index}`}>
                          <Checkbox
                            checked={selectedDomains.includes(domainName)}
                            onChange={() => handleDomainToggle(domainName)}
                            data-testid={`wizard-step1-policy-creation-domain-checkbox-${index}`}
                          />
                          <Text className={styles.domainName} data-testid={`wizard-step1-policy-creation-domain-name-${index}`}>{domainName}</Text>
                          {domain.organization && (
                            <Text className={styles.domainOrg} data-testid={`wizard-step1-policy-creation-domain-org-${index}`}>
                              ({domain.organization})
                            </Text>
                          )}
                        </div>
                      );
                    })}
                </div>
              </>
            ) : (
              <div className={styles.noDomains} data-testid="wizard-step1-policy-creation-no-domains">
                <Text data-testid="wizard-step1-policy-creation-no-domains-text">No approved domains available.</Text>
                <Text style={{ fontSize: '12px', marginTop: '8px' }} data-testid="wizard-step1-policy-creation-no-domains-help-text">
                  Go to the Domains page to approve domains from the domain feed first.
                </Text>
              </div>
            )}
          </div>
        )}
      </div>

      {/* GFC-Specific Sections */}
      {isGFCMode && (
        <div className={styles.gfcSections} data-testid="wizard-step1-policy-creation-gfc-sections">
          {/* Existing Policy Check */}
          <Card data-testid="wizard-step1-policy-creation-existing-policy-card">
            <CardHeader
              header={<Text weight="semibold" size={500} data-testid="wizard-step1-policy-creation-existing-policy-title">Existing Policy Review</Text>}
            />
            <div style={{ padding: '0 16px 16px' }} data-testid="wizard-step1-policy-creation-div-7">
              <ExistingPolicySelector
                selectedPolicy={selectedExistingPolicy}
                onPolicySelect={setSelectedExistingPolicy}
                showSelection={false}
              />
            </div>
          </Card>

          {/* Communication Mode Selection */}
          <CommunicationModeSelector
            value={communicationMode}
            onChange={setCommunicationMode}
          />

          {/* Domain Management */}
          <EnhancedDomainManager
            allowedDomains={allowedDomains}
            blockedDomains={blockedDomains}
            onAllowedDomainsChange={setAllowedDomains}
            onBlockedDomainsChange={setBlockedDomains}
            communicationMode={communicationMode}
          />

          {/* Domain Optimization */}
          {getTotalDomains() > 0 && (
            <DomainOptimizer
              domains={[...allowedDomains, ...blockedDomains]}
              autoOptimize={getTotalDomains() > 80}
            />
          )}

          {/* Risk Category */}
          <RiskCategorySelector
            value={riskCategory}
            onChange={setRiskCategory}
            complianceRegion={complianceRegion}
          />

          {/* Compliance Region */}
          <Field label="Compliance Region:" data-testid="wizard-step1-policy-creation-compliance-region-field">
            <RadioGroup 
              value={complianceRegion}
              onChange={(e, data) => setComplianceRegion(data.value as 'EU' | 'US' | 'APAC' | 'Global')}
              data-testid="wizard-step1-policy-creation-compliance-region-group"
            >
              <Radio value="EU" label="European Union (GDPR)" data-testid="wizard-step1-policy-creation-compliance-region-eu" />
              <Radio value="US" label="United States (SOX, FINRA)" data-testid="wizard-step1-policy-creation-compliance-region-us" />
              <Radio value="APAC" label="Asia Pacific" data-testid="wizard-step1-policy-creation-compliance-region-apac" />
              <Radio value="Global" label="Global/Multi-Region" data-testid="wizard-step1-policy-creation-compliance-region-global" />
            </RadioGroup>
          </Field>
        </div>
      )}

      <div className={styles.buttonContainer} data-testid="wizard-step1-policy-creation-button-container">
        <Button
          appearance="primary"
          onClick={() => {
            handleNext()
            handleActionOnClick(ElementButton.ADD_POLICY, selectedUnmanagedPolicyId, 'group', selectedEntities.map(item => item.id), selectedDomains, selectedUnmanagedPolicy, enableBilateralChat ? 'ON' : 'OFF');
          }
        }
          disabled={!canProceed || isCreating || loadingWorkflowAnalysis}
          data-testid="wizard-step1-policy-creation-add-policy"
        >
          {loadingWorkflowAnalysis ? (
            <>
              <Spinner size="tiny" style={{ marginRight: '8px' }} data-testid="wizard-step1-policy-creation-analyzing-spinner" />
              Analyzing...
            </>
          ) : (
            'Add Policy'
          )}
        </Button>
      </div>

      {/* Warning Dialog for User Assignments */}
      <Dialog
        open={showUserAssignmentWarning}
        onOpenChange={(event, data) => setShowUserAssignmentWarning(data.open)}
        data-testid="wizard-step1-policy-creation-user-assignment-warning-dialog"
      >
        <DialogSurface data-testid="wizard-step1-policy-creation-user-assignment-warning-surface">
          <DialogBody data-testid="wizard-step1-policy-creation-user-assignment-warning-body">
            <DialogTitle data-testid="wizard-step1-policy-creation-user-assignment-warning-title">Warning: User Assignments Detected</DialogTitle>
            <DialogContent data-testid="wizard-step1-policy-creation-user-assignment-warning-content">
              <div style={{ marginBottom: '16px' }} data-testid="wizard-step1-policy-creation-div-8">
                <Text weight="semibold" data-testid="wizard-step1-policy-creation-user-assignment-warning-summary-text">
                  The policy "{warningDialogData?.policyName}" has {warningDialogData?.userCount} user assignment{warningDialogData?.userCount !== 1 ? 's' : ''}.
                </Text>
              </div>

              <div style={{ marginBottom: '16px' }} data-testid="wizard-step1-policy-creation-div-9">
                <Text data-testid="wizard-step1-policy-creation-user-assignment-warning-description-text">
                  These user assignments should be removed before creating a managed policy. User assignments on the unmanaged policy may cause conflicts.
                </Text>
              </div>

              {warningDialogData?.users && warningDialogData.users.length > 0 && (
                <div style={{ marginTop: '12px' }} data-testid="wizard-step1-policy-creation-div-10">
                  <Text weight="semibold" data-testid="wizard-step1-policy-creation-user-assignment-users-label">Users assigned:</Text>
                  <ul style={{ marginTop: '8px', marginBottom: '0' }} data-testid="wizard-step1-policy-creation-ul">
                    {warningDialogData.users.map((user, index) => (
                      <li key={index} data-testid="wizard-step1-policy-creation-li">{user}</li>
                    ))}
                    {warningDialogData.userCount > 5 && (
                      <li data-testid="wizard-step1-policy-creation-li-2">...and {warningDialogData.userCount - 5} more</li>
                    )}
                  </ul>
                </div>
              )}

              <div style={{ marginTop: '16px' }} data-testid="wizard-step1-policy-creation-div-11">
                <Text size={300} style={{ fontStyle: 'italic' }} data-testid="wizard-step1-policy-creation-user-assignment-warning-note-text">
                  You can proceed, but it's recommended to remove these assignments first.
                </Text>
              </div>
            </DialogContent>
            <DialogActions data-testid="wizard-step1-policy-creation-user-assignment-warning-actions">
              <Button
                appearance="primary"
                onClick={() => setShowUserAssignmentWarning(false)}
                data-testid="wizard-step1-policy-creation-user-assignment-warning-ok"
              >
                OK
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Loading Dialog - Shows while analyzing assignments */}
      <Dialog
        open={loadingWorkflowAnalysis && !showWorkflowDialog}
        modalType="modal"
        data-testid="wizard-step1-policy-creation-loading-workflow-dialog"
      >
        <DialogSurface data-testid="wizard-step1-policy-creation-loading-workflow-surface">
          <DialogBody data-testid="wizard-step1-policy-creation-loading-workflow-body">
            <DialogTitle data-testid="wizard-step1-policy-creation-loading-workflow-title">Analyzing Policy Assignments</DialogTitle>
            <DialogContent data-testid="wizard-step1-policy-creation-loading-workflow-content">
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
                padding: '24px'
              }} data-testid="wizard-step1-policy-creation-div-12">
                <Spinner size="huge" data-testid="wizard-step1-policy-creation-loading-workflow-spinner" />
                <Text size={400} weight="semibold" data-testid="wizard-step1-policy-creation-loading-workflow-main-text">
                  Analyzing assignments from Microsoft Teams...
                </Text>
                <Text size={300} style={{ color: '#605e5c', textAlign: 'center' }} data-testid="wizard-step1-policy-creation-loading-workflow-subtitle-text">
                  This may take several seconds while we:
                </Text>
                <ul style={{ textAlign: 'left', fontSize: '14px', color: '#605e5c' }} data-testid="wizard-step1-policy-creation-ul-2">
                  <li data-testid="wizard-step1-policy-creation-li-3">Authenticate to Microsoft Teams</li>
                  <li data-testid="wizard-step1-policy-creation-li-4">Fetch group assignments</li>
                  <li data-testid="wizard-step1-policy-creation-li-5">Fetch user assignments</li>
                  <li data-testid="wizard-step1-policy-creation-li-6">Retrieve group details from Microsoft Graph</li>
                </ul>
              </div>
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Assignment Workflow Analysis Dialog */}
      <Dialog
        open={showWorkflowDialog}
        onOpenChange={(event, data) => setShowWorkflowDialog(data.open)}
        data-testid="wizard-step1-policy-creation-workflow-dialog"
      >
        <DialogSurface style={{ maxWidth: '900px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }} data-testid="wizard-step1-policy-creation-workflow-surface">
          <DialogBody style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }} data-testid="wizard-step1-policy-creation-workflow-body">
            <DialogTitle data-testid="wizard-step1-policy-creation-workflow-title">Assignment Workflow Analysis Results</DialogTitle>
            {/* Header label for scrollable section */}
            <div style={{
              padding: '8px 12px',
              backgroundColor: '#f0f8ff',
              borderRadius: '4px',
              marginBottom: '8px',
              border: '1px solid #0078d4',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }} data-testid="wizard-step1-policy-creation-div-13">
              <Text weight="semibold" size={300} style={{ color: '#0078d4' }} data-testid="wizard-step1-policy-creation-workflow-scroll-label">
                📊 Analysis Details (scroll to view all)
              </Text>
            </div>

            <DialogContent style={{
              flex: '1 1 auto',
              overflowY: 'auto',
              marginBottom: '16px',
              paddingRight: '8px',
              border: '1px solid #d1d1d1',
              borderRadius: '4px',
              backgroundColor: '#fafafa',
              maxHeight: '550px',
              position: 'relative'
            }} data-testid="wizard-step1-policy-creation-workflow-content">
              {workflowAnalysisData && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '12px' }} data-testid="wizard-step1-policy-creation-div-14">
                  {/* NEW SECURITY GROUP - Prominently displayed at top */}
                  {workflowAnalysisData.newSecurityGroup && (
                    <Card style={{ border: '2px solid #0078d4', backgroundColor: '#f0f8ff' }} data-testid="wizard-step1-policy-creation-new-security-group-card">
                      <CardHeader
                        header={<Text weight="bold" size={500} style={{ color: '#0078d4' }} data-testid="wizard-step1-policy-creation-new-security-group-title">🔒 New Security Group to be Created</Text>}
                      />
                      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }} data-testid="wizard-step1-policy-creation-div-15">
                        <Text size={400} data-testid="wizard-step1-policy-creation-new-security-group-name-text"><strong data-testid="wizard-step1-policy-creation-strong-4">Group Name:</strong> {workflowAnalysisData.newSecurityGroup.name}</Text>
                        <Text size={400} data-testid="wizard-step1-policy-creation-new-security-group-count-text"><strong data-testid="wizard-step1-policy-creation-strong-5">Total Members:</strong> {workflowAnalysisData.newSecurityGroup.memberCount}</Text>
                        <Text size={300} style={{ color: '#605e5c', fontStyle: 'italic' }} data-testid="wizard-step1-policy-creation-new-security-group-description-text">
                          {workflowAnalysisData.newSecurityGroup.description}
                        </Text>

                        {workflowAnalysisData.newSecurityGroup.memberCount > 0 && (
                          <div style={{ marginTop: '8px' }} data-testid="wizard-step1-policy-creation-div-16">
                            <Text weight="semibold" size={300} data-testid="wizard-step1-policy-creation-new-security-group-members-label">Members ({workflowAnalysisData.newSecurityGroup.memberCount}):</Text>
                            <div style={{
                              maxHeight: '200px',
                              overflowY: 'auto',
                              marginTop: '8px',
                              padding: '12px',
                              backgroundColor: 'white',
                              borderRadius: '4px',
                              border: '1px solid #d1d1d1'
                            }} data-testid="wizard-step1-policy-creation-div-17">
                              {workflowAnalysisData.newSecurityGroup.members.map((email: string, index: number) => (
                                <div key={index} style={{ padding: '4px 0', fontSize: '13px' }} data-testid="wizard-step1-policy-creation-div-18">
                                  {index + 1}. {email}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {workflowAnalysisData.analysis?.usersNotInDefaultGroup > 0 && (
                          <div style={{
                            marginTop: '8px',
                            padding: '12px',
                            backgroundColor: '#fff4e5',
                            borderRadius: '4px',
                            border: '1px solid #ffa500'
                          }} data-testid="wizard-step1-policy-creation-div-19">
                            <Text size={300} style={{ color: '#8a5700' }} data-testid="wizard-step1-policy-creation-new-security-group-exclusion-warning-text">
                              ⚠️ <strong data-testid="wizard-step1-policy-creation-strong-6">{workflowAnalysisData.analysis.usersNotInDefaultGroup} user(s)</strong> assigned to the parent policy are NOT in "{workflowAnalysisData.defaultGroup?.displayName}" and will be excluded from the new group.
                            </Text>
                          </div>
                        )}
                      </div>
                    </Card>
                  )}

                  {/* Policy Information */}
                  <Card data-testid="wizard-step1-policy-creation-policy-information-card">
                    <CardHeader
                      header={<Text weight="semibold" data-testid="wizard-step1-policy-creation-policy-information-title">Policy Information</Text>}
                    />
                    <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }} data-testid="wizard-step1-policy-creation-div-20">
                      <Text data-testid="wizard-step1-policy-creation-policy-name-text"><strong data-testid="wizard-step1-policy-creation-strong-7">Policy Name:</strong> {workflowAnalysisData.policyName}</Text>
                      <Text data-testid="wizard-step1-policy-creation-org-policy-setting-text"><strong data-testid="wizard-step1-policy-creation-strong-8">Org Policy Setting:</strong> {workflowAnalysisData.orgPolicySetting}</Text>
                      <Text data-testid="wizard-step1-policy-creation-recommended-adm-setting-text"><strong data-testid="wizard-step1-policy-creation-strong-9">Recommended ADM Setting:</strong> {workflowAnalysisData.admPolicySetting}</Text>
                    </div>
                  </Card>

                  {/* Parent Policy Assignments */}
                  <Card data-testid="wizard-step1-policy-creation-parent-assignments-card">
                    <CardHeader
                      header={<Text weight="semibold" data-testid="wizard-step1-policy-creation-parent-assignments-title">Parent Policy Assignments</Text>}
                    />
                    <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }} data-testid="wizard-step1-policy-creation-div-21">
                      <div data-testid="wizard-step1-policy-creation-div-22">
                        <Text weight="semibold" data-testid="wizard-step1-policy-creation-parent-groups-label">Groups ({workflowAnalysisData.parentPolicyAssignments?.groups?.length || 0}):</Text>
                        {workflowAnalysisData.parentPolicyAssignments?.groups && workflowAnalysisData.parentPolicyAssignments.groups.length > 0 ? (
                          <ul style={{ marginTop: '8px', marginBottom: '0' }} data-testid="wizard-step1-policy-creation-ul-3">
                            {workflowAnalysisData.parentPolicyAssignments.groups.map((group: any, index: number) => (
                              <li key={index} data-testid="wizard-step1-policy-creation-li-7">{group.displayName}</li>
                            ))}
                          </ul>
                        ) : (
                          <Text style={{ color: '#605e5c', fontSize: '14px', marginTop: '4px' }} data-testid="wizard-step1-policy-creation-parent-groups-empty-text">No group assignments</Text>
                        )}
                      </div>
                      <div data-testid="wizard-step1-policy-creation-div-23">
                        <Text weight="semibold" data-testid="wizard-step1-policy-creation-parent-users-label">Direct Users ({workflowAnalysisData.parentPolicyAssignments?.users?.length || 0}):</Text>
                        {workflowAnalysisData.parentPolicyAssignments?.users && workflowAnalysisData.parentPolicyAssignments.users.length > 0 ? (
                          <ul style={{ marginTop: '8px', marginBottom: '0' }} data-testid="wizard-step1-policy-creation-ul-4">
                            {workflowAnalysisData.parentPolicyAssignments.users.slice(0, 10).map((user: any, index: number) => (
                              <li key={index} data-testid="wizard-step1-policy-creation-li-8">{user.displayName} ({user.userEmail})</li>
                            ))}
                            {workflowAnalysisData.parentPolicyAssignments.users.length > 10 && (
                              <li data-testid="wizard-step1-policy-creation-li-9">...and {workflowAnalysisData.parentPolicyAssignments.users.length - 10} more</li>
                            )}
                          </ul>
                        ) : (
                          <Text style={{ color: '#605e5c', fontSize: '14px', marginTop: '4px' }} data-testid="wizard-step1-policy-creation-parent-users-empty-text">No direct user assignments</Text>
                        )}
                      </div>
                      <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }} data-testid="wizard-step1-policy-creation-div-24">
                        <Text size={300} data-testid="wizard-step1-policy-creation-total-assigned-users-text">
                          <strong data-testid="wizard-step1-policy-creation-strong-10">Total assigned users (expanded):</strong> {workflowAnalysisData.analysis?.totalAssignedUsers || 0}
                        </Text>
                      </div>
                    </div>
                  </Card>

                  {/* Domain Lists */}
                  <Card data-testid="wizard-step1-policy-creation-domain-configuration-card">
                    <CardHeader
                      header={<Text weight="semibold" data-testid="wizard-step1-policy-creation-domain-configuration-title">Domain Configuration</Text>}
                    />
                    <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }} data-testid="wizard-step1-policy-creation-div-25">
                      <div data-testid="wizard-step1-policy-creation-div-26">
                        <Text weight="semibold" data-testid="wizard-step1-policy-creation-org-policy-domains-label">Org Policy Domains ({workflowAnalysisData.orgPolicyDomains?.length || 0}):</Text>
                        {workflowAnalysisData.orgPolicyDomains && workflowAnalysisData.orgPolicyDomains.length > 0 ? (
                          <div style={{ maxHeight: '150px', overflowY: 'auto', marginTop: '8px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }} data-testid="wizard-step1-policy-creation-div-27">
                            {workflowAnalysisData.orgPolicyDomains.map((domain: string, index: number) => (
                              <div key={index} style={{ padding: '4px 0' }} data-testid="wizard-step1-policy-creation-div-28">{domain}</div>
                            ))}
                          </div>
                        ) : (
                          <Text style={{ color: '#605e5c', fontSize: '14px', marginTop: '4px' }} data-testid="wizard-step1-policy-creation-org-policy-domains-empty-text">No org policy domains</Text>
                        )}
                      </div>
                      <div data-testid="wizard-step1-policy-creation-div-29">
                        <Text weight="semibold" data-testid="wizard-step1-policy-creation-external-feed-domains-label">External Feed Domains ({workflowAnalysisData.externalFeedDomains?.length || 0}):</Text>
                        {workflowAnalysisData.externalFeedDomains && workflowAnalysisData.externalFeedDomains.length > 0 ? (
                          <div style={{ maxHeight: '150px', overflowY: 'auto', marginTop: '8px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }} data-testid="wizard-step1-policy-creation-div-30">
                            {workflowAnalysisData.externalFeedDomains.map((domain: string, index: number) => (
                              <div key={index} style={{ padding: '4px 0' }} data-testid="wizard-step1-policy-creation-div-31">{domain}</div>
                            ))}
                          </div>
                        ) : (
                          <Text style={{ color: '#605e5c', fontSize: '14px', marginTop: '4px' }} data-testid="wizard-step1-policy-creation-external-feed-domains-empty-text">No external feed domains</Text>
                        )}
                      </div>
                      <div data-testid="wizard-step1-policy-creation-div-32">
                        <Text weight="semibold" style={{ color: '#0078d4' }} data-testid="wizard-step1-policy-creation-recommended-adm-domains-label">Recommended ADM Policy Domains ({workflowAnalysisData.updatedAdmDomains?.length || 0}):</Text>
                        {workflowAnalysisData.updatedAdmDomains && workflowAnalysisData.updatedAdmDomains.length > 0 ? (
                          <div style={{ maxHeight: '150px', overflowY: 'auto', marginTop: '8px', padding: '8px', backgroundColor: '#e6f4ff', borderRadius: '4px', border: '1px solid #0078d4' }} data-testid="wizard-step1-policy-creation-div-33">
                            {workflowAnalysisData.updatedAdmDomains.map((domain: string, index: number) => (
                              <div key={index} style={{ padding: '4px 0' }} data-testid="wizard-step1-policy-creation-div-34">{domain}</div>
                            ))}
                          </div>
                        ) : (
                          <Text style={{ color: '#605e5c', fontSize: '14px', marginTop: '4px' }} data-testid="wizard-step1-policy-creation-recommended-adm-domains-empty-text">
                            {workflowAnalysisData.admPolicySetting === 'AllowAllExternalDomains' ? 'All domains allowed (no filtering)' : 'No domains'}
                          </Text>
                        )}
                      </div>
                    </div>
                  </Card>

                  {/* Matching Information */}
                  {workflowAnalysisData.matchingInfo && (
                    <Card data-testid="wizard-step1-policy-creation-matching-info-card">
                      <CardHeader
                        header={<Text weight="semibold" data-testid="wizard-step1-policy-creation-matching-info-title">All Directory Users Group Matching</Text>}
                      />
                      <div style={{ padding: '12px' }} data-testid="wizard-step1-policy-creation-div-35">
                        <Text data-testid="wizard-step1-policy-creation-default-group-members-text"><strong data-testid="wizard-step1-policy-creation-strong-11">Default Group Members:</strong> {workflowAnalysisData.matchingInfo.defaultGroupMemberCount || 0}</Text>
                        <br data-testid="wizard-step1-policy-creation-br-7" />
                        <Text data-testid="wizard-step1-policy-creation-matching-users-text"><strong data-testid="wizard-step1-policy-creation-strong-12">Matching Users:</strong> {workflowAnalysisData.matchingInfo.matchingUserCount || 0}</Text>
                        <br data-testid="wizard-step1-policy-creation-br-8" />
                        <Text data-testid="wizard-step1-policy-creation-non-matching-users-text"><strong data-testid="wizard-step1-policy-creation-strong-13">Non-Matching Users:</strong> {workflowAnalysisData.matchingInfo.nonMatchingUserCount || 0}</Text>
                      </div>
                    </Card>
                  )}
                </div>
              )}
              {/* Scroll indicator */}
              <div style={{
                position: 'sticky',
                bottom: 0,
                left: 0,
                right: 0,
                height: '32px',
                background: 'linear-gradient(to bottom, transparent, rgba(250, 250, 250, 0.9) 50%, rgba(250, 250, 250, 1))',
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                paddingBottom: '4px',
                pointerEvents: 'none'
              }} data-testid="wizard-step1-policy-creation-div-36">
                <Text size={200} style={{ color: '#605e5c', fontStyle: 'italic' }} data-testid="wizard-step1-policy-creation-scroll-indicator-text">
                  ↓ Scroll for more details ↓
                </Text>
              </div>
            </DialogContent>
            <DialogActions style={{
              flexShrink: 0,
              borderTop: '1px solid #e1dfdd',
              paddingTop: '16px',
              marginTop: '0'
            }} data-testid="wizard-step1-policy-creation-workflow-actions">
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                width: '100%'
              }} data-testid="wizard-step1-policy-creation-div-37">
                {/* Information header */}
                <div style={{
                  padding: '12px',
                  backgroundColor: '#f0f8ff',
                  borderRadius: '4px',
                  border: '1px solid #0078d4'
                }} data-testid="wizard-step1-policy-creation-div-38">
                  <Text weight="semibold" size={400} style={{ display: 'block', marginBottom: '8px' }} data-testid="wizard-step1-policy-creation-assignment-method-title">
                    Choose Assignment Method:
                  </Text>
                  <Text size={300} data-testid="wizard-step1-policy-creation-assignment-method-description">
                    Select how you want to assign this policy to users.
                  </Text>
                </div>

                {/* Two-column layout for options */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '12px'
                }} data-testid="wizard-step1-policy-creation-div-39">
                  {/* Option A: Create ADM Security Group */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    padding: '10px',
                    backgroundColor: '#f0f8ff',
                    border: '2px solid #0078d4',
                    borderRadius: '6px'
                  }} data-testid="wizard-step1-policy-creation-div-40">
                    <Text weight="semibold" size={300} style={{ color: '#0078d4' }} data-testid="wizard-step1-policy-creation-option-a-title">
                      Option A: Create ADM Security Group
                    </Text>
                    <Badge appearance="filled" color="brand" size="small" style={{ width: 'fit-content' }} data-testid="wizard-step1-policy-creation-option-a-recommended-badge">
                      Recommended
                    </Badge>
                    <Text size={200} style={{ marginBottom: '2px', lineHeight: '1.2' }} data-testid="wizard-step1-policy-creation-option-a-description">
                      Creates new security group with users in both parent policy AND default group. Cleaner management.
                    </Text>
                    <Button
                      appearance="primary"
                      onClick={handleCreateSecurityGroupAndApply}
                      disabled={creatingSecurityGroup || !workflowAnalysisData?.newSecurityGroup}
                      style={{ width: '100%', marginTop: '4px' }}
                      size="small"
                      data-testid="wizard-step1-policy-creation-create-security-group"
                    >
                      {creatingSecurityGroup ? (
                        <>
                          <Spinner size="tiny" style={{ marginRight: '8px' }} data-testid="wizard-step1-policy-creation-create-security-group-spinner" />
                          Creating...
                        </>
                      ) : (
                        'Create Security Group'
                      )}
                    </Button>
                  </div>

                  {/* Option B: Use Individual User Assignments */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    padding: '10px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #d1d1d1',
                    borderRadius: '6px'
                  }} data-testid="wizard-step1-policy-creation-div-41">
                    <Text weight="semibold" size={300} data-testid="wizard-step1-policy-creation-option-b-title">
                      Option B: Individual User Assignments
                    </Text>
                    <div style={{ height: '18px' }} data-testid="wizard-step1-policy-creation-div-42">{/* Spacer for alignment */}</div>
                    <Text size={200} style={{ marginBottom: '2px', lineHeight: '1.2' }} data-testid="wizard-step1-policy-creation-option-b-description">
                      Assigns to default group plus all individual users from parent policy. Maintains existing assignments.
                    </Text>
                    <Button
                      appearance="secondary"
                      onClick={handleUseIndividualAssignments}
                      disabled={creatingSecurityGroup || isCreating}
                      style={{ width: '100%', marginTop: '4px' }}
                      size="small"
                      data-testid="wizard-step1-policy-creation-use-individual-assignments"
                    >
                      {isCreating ? (
                        <>
                          <Spinner size="tiny" style={{ marginRight: '8px' }} data-testid="wizard-step1-policy-creation-use-individual-assignments-spinner" />
                          Creating...
                        </>
                      ) : (
                        'Use Individual Assignments'
                      )}
                    </Button>
                  </div>
                </div>

                {/* Cancel button */}
                <Button
                  appearance="subtle"
                  onClick={() => setShowWorkflowDialog(false)}
                  disabled={creatingSecurityGroup}
                  style={{ width: '100%' }}
                  data-testid="wizard-step1-policy-creation-workflow-cancel"
                >
                  Cancel
                </Button>
              </div>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Security Group Creation Success Dialog */}
      <Dialog
        open={showSecurityGroupSuccessDialog}
        onOpenChange={(event, data) => setShowSecurityGroupSuccessDialog(data.open)}
        data-testid="wizard-step1-policy-creation-security-group-success-dialog"
      >
        <DialogSurface style={{ maxWidth: '600px' }} data-testid="wizard-step1-policy-creation-security-group-success-surface">
          <DialogBody data-testid="wizard-step1-policy-creation-security-group-success-body">
            <DialogTitle data-testid="wizard-step1-policy-creation-security-group-success-title">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#107c10' }} data-testid="wizard-step1-policy-creation-div-43">
                <CheckmarkCircle24Filled />
                <span data-testid="wizard-step1-policy-creation-span">Security Group Created Successfully</span>
              </div>
            </DialogTitle>
            <DialogContent data-testid="wizard-step1-policy-creation-security-group-success-content">
              {securityGroupSuccessData && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }} data-testid="wizard-step1-policy-creation-div-44">
                  {/* Success Summary */}
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#f0f9ff',
                    borderRadius: '8px',
                    border: '1px solid #0078d4'
                  }} data-testid="wizard-step1-policy-creation-div-45">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }} data-testid="wizard-step1-policy-creation-div-46">
                      <PeopleTeam24Regular style={{ color: '#0078d4', fontSize: '32px' }} />
                      <div data-testid="wizard-step1-policy-creation-div-47">
                        <Text size={500} weight="semibold" style={{ display: 'block' }} data-testid="wizard-step1-policy-creation-security-group-name-text">
                          {securityGroupSuccessData.displayName}
                        </Text>
                        <Text size={300} style={{ color: '#605e5c' }} data-testid="wizard-step1-policy-creation-security-group-count-text">
                          {securityGroupSuccessData.membersAdded} member{securityGroupSuccessData.membersAdded !== 1 ? 's' : ''} added
                        </Text>
                      </div>
                    </div>
                    <Text size={300} style={{ color: '#323130' }} data-testid="wizard-step1-policy-creation-security-group-summary-text">
                      The security group has been successfully created in Azure AD and assigned to your policy configuration.
                    </Text>
                  </div>

                  {/* Members List */}
                  {securityGroupSuccessData.members && securityGroupSuccessData.members.length > 0 && (
                    <div data-testid="wizard-step1-policy-creation-div-48">
                      <Text weight="semibold" size={400} style={{ marginBottom: '8px', display: 'block' }} data-testid="wizard-step1-policy-creation-security-group-members-title">
                        Group Members:
                      </Text>
                      <div style={{
                        maxHeight: '300px',
                        overflowY: 'auto',
                        padding: '12px',
                        backgroundColor: '#fafafa',
                        borderRadius: '4px',
                        border: '1px solid #e1dfdd'
                      }} data-testid="wizard-step1-policy-creation-div-49">
                        {securityGroupSuccessData.members.map((member: string, index: number) => (
                          <div key={index} style={{
                            padding: '8px',
                            marginBottom: index < securityGroupSuccessData.members.length - 1 ? '4px' : '0',
                            backgroundColor: 'white',
                            borderRadius: '4px',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }} data-testid="wizard-step1-policy-creation-div-50">
                            <Badge appearance="filled" color="brand" size="small" data-testid={`wizard-step1-policy-creation-security-group-member-index-${index + 1}`}>
                              {index + 1}
                            </Badge>
                            <Text size={300} data-testid={`wizard-step1-policy-creation-security-group-member-${index + 1}`}>{member}</Text>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Next Steps */}
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#fff4e5',
                    borderRadius: '4px',
                    border: '1px solid #ffc107'
                  }} data-testid="wizard-step1-policy-creation-div-51">
                    <Text size={300} weight="semibold" style={{ display: 'block', marginBottom: '8px' }} data-testid="wizard-step1-policy-creation-next-steps-title">
                      📌 Next Steps:
                    </Text>
                    <Text size={300} data-testid="wizard-step1-policy-creation-next-steps-description">
                      You can now proceed to create the policy. The security group has been automatically assigned to your policy configuration.
                    </Text>
                  </div>
                </div>
              )}
            </DialogContent>
            <DialogActions data-testid="wizard-step1-policy-creation-security-group-success-actions">
              <Button
                appearance="primary"
                onClick={() => setShowSecurityGroupSuccessDialog(false)}
                data-testid="wizard-step1-policy-creation-security-group-success-continue"
              >
                Continue
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};